import { DCFlashcard } from '@dataclouder/dc-flashcard';
import { TranscriptionsWhisper } from '@dataclouder/ngx-agent-cards'; // Import TranscriptionsWhisper
import { IAuditable, ILearnable } from '@dataclouder/ngx-core';
import { FailReason, ILearningUserState } from '@dataclouder/ngx-knowledge';

import { AudioStorage, LearningActivityType, StorageFile } from '../classes';

export interface TranslationData {
  translation: string;
  translations: Array<string>;
  meaningTranslation?: string; // Only for translations that need explanation
}
export interface AllLanguageTranslation {
  es: TranslationData;
  en: TranslationData;
  it: TranslationData;
  pt: TranslationData;
  fr: TranslationData;
  ja: TranslationData;
  ko: TranslationData;
  zh: TranslationData;
  ar: TranslationData;
  ru: TranslationData;
  de: TranslationData;
  hi: TranslationData;
  bn: TranslationData;
  sw: TranslationData;
  ur: TranslationData;
  id: TranslationData;
}

export const LevelDescription = {
  1: 'Beginner',
  2: 'Beginner Pre-Intermediate',
  3: 'Intermediate',
  4: 'Upper-Intermediate',
  5: 'Advanced and Proficient',
  6: 'Master',
};
export class AuditData {
  createdDate: string;
  modifiedDate: string;
}

export type ValidTables = 'word_en' | 'word_es' | 'word_it' | 'word_pt' | 'word_fr' | 'word_ja';

export type ValidNodeTables =
  | 'knowledgeState.en.word'
  | 'knowledgeState.es.word'
  | 'knowledgeState.it.word'
  | 'knowledgeState.pt.word'
  | 'knowledgeState.fr.word'
  | 'knowledgeState.ja.word';

export class UserExtraData extends AuditData {
  progress: number;
  completed: boolean;
}

export interface IWordBase {
  _id: string;
  id: string;
  word: string;
  synonyms?: Array<string>;
  antonyms?: Array<string>;
  definitions?: Array<string>;
  examples?: Array<string>;

  derivedWords?: Array<string>;
  inflections?: Array<string>;
  tags?: Array<string>;
  collocations?: Array<string>;
  etymology?: string;

  hispanicPhonetic?: string;
  ipaPhonetic?: string;
  respelledPhonetic?: string;

  wordCategory?: string; // before partOfSpeech
  lemma?: string;
  baseVerb?: string;
  useFrequency?: number;

  audios?: Array<AudioStorage>;

  learningExamples?: Array<LearningExample>;
  langTranslation: AllLanguageTranslation;

  translation?: string; // @Deprecated
  otherTranslations?: Array<string>; // @Deprecated

  flashcards?: Array<DCFlashcard>;
  level: number;
  extra?: Record<string, unknown>;

  // Features
  learnable: ILearnable;
  auditData?: IAuditable;
  // reactions?: IReactable;

  // @Deprecated soon
  es: { translation: string; otherTranslations: Array<string> };
  en: { translation: string; otherTranslations: Array<string> };
  audioUrl: string; // I use but not sure why
}

export interface JapaneseExtra {
  kanji: string;
  hiragana: string;
  katakana: string;
  romaji: string;
  kanjiExample: string;
  hiraganaExample: string;
  katakanaExample: string;
  romajiExample: string;
  spanishTranslation: string;
  englishTranslation: string;
}

export const JapaneseExtraString = `
{ 
  extra: {
    kanji: string;
    hiragana: string;
    katakana: string;
    romaji: string;
    kanjiExample: string;
    hiraganaExample: string;
    katakanaExample: string;
    romajiExample: string;
    spanishTranslation: string;
    englishTranslation: string;
  }
}`;

// This is interface for AI models.
export const LangWordStringInterface: string = `
{
  word: string;              // word to translate
  synonyms: Array<string>;   // synonyms of the word
  antonyms: Array<string>;   // antonyms of the word
  definitions: Array<string>; // definitions of the word
  examples: Array<string>;   // examples of the word

  hispanicPhonetic: string; // hispanic phonetic of the word
  ipaPhonetic: string;      // ipa phonetic of the word
  respelledPhonetic: string; // respelled phonetic of the word

  wordCategory: string; // category of the word like noun, verb, adjective, adverb, pronoun, preposition, conjunction, interjection, determiner
  lemma: string;         // base/dictionary form of a word 
  baseVerb: string;      // base verb of the word when word is verb derived else empty
  useFrequency: number;     // use frequency of the word from 1 to 5, five is the most used
  etymology: string;     // word origin and historical development
  collocations: Array<string>; // common word combinations
  tags: Array<string>; // topic tags (medical, legal, cooking, etc.)
  inflections: Array<string>; // inflections of the word, grammatical changes to the SAME word - same meaning, different form for grammar, example walk -> walks, walking, walked, etc.
  derivedWords: Array<string>; // derived words are NEW words created from the base word - often different meaning/part of speech, example walk -> walking, walker, walkable, etc.
}
`;
// For future use this type
export type UserWord = IWordBase & UserExtraData;

export class QuickPhrase {
  id: string;
  phrase: string;
  translation: { es: string; en: string };
  url: string;
  transcription: TranscriptionsWhisper; // Use specific type
}

export class LearningExample {
  _id: string; // id del learningExample de mongo.
  id: string;
  word?: string; // TODO: Sería más facil si word existe aunque este duplicado.
  example: string;
  meaning: string;
  scenario: string;
  picture: string;

  meaningTranslation: string; // reserved for base language

  // @Deprecated soon
  es: { translation: string; meaningTranslation: string };
  en: { translation: string; meaningTranslation: string };

  audio: AudioStorage;
  translation?: string; // @Deprecated soon will moved to AudioStorage

  image: StorageFile & { text: string };

  attribution: string;
  generatedByAI: boolean;
  author: string;
  difficultyLevel: string; // Deprecated
  level: string;
  tense?: string; // will help to practice verbs
  isPublic: boolean;
  quickPhrases: QuickPhrase[];
}

export interface IWordQueryMultiBases {
  word: string;
  otherBases: { lang: string; objectPath: string; dbPath: string }[];
}

export interface WordSavedRes {
  word: IWordBase;
  knowledgeState: ILearningUserState;
}

export interface WordProgressTrack {
  word: string;
  sucess: boolean;
  points: number;
  failReason: FailReason;
  learningActivity: LearningActivityType;
}

export interface UserRecommendations {
  words: {
    words: IWordBase[];
  };
}

export interface UserSaveWordsResponse {
  // savedWords: IWordBase[];
  removedFromRecommended: string[];
  sourceCore: IWordBase[];
  userState: ILearningUserState[];
  mongoResponse: unknown;
}

export interface EnglishVerb extends IWordBase {
  past: string;
  present: string;
  future: string;
  pastParticiple: string;
  participle: string;
  verb: string;
  isCustom: boolean;
}
